// Shared utility functions for use across all components
import axios from 'axios';

/**
 * Endpoint that receives contact-form submissions.
 * Set REACT_APP_CONTACT_API_URL at build time to override (for example '/zemantics/api/email/contact').
 * Defaults to the local Node server in development and to '/api/email/contact' on the same origin in production.
 */
export const CONTACT_API_URL: string =
  process.env.REACT_APP_CONTACT_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api/email/contact' : 'http://localhost:5000/api/email/contact');

/**
 * Scrolls the window to the top of the page
 */
export const scrollToTop = (): void => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  });
};

/**
 * Formats a number with commas for better readability
 * @param num - The number to format
 * @returns Formatted number string
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

/**
 * Capitalizes the first letter of a string
 * @param str - The string to capitalize
 * @returns Capitalized string
 */
export const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Truncates text to a specified length and adds ellipsis
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Debounces a function call to prevent excessive executions
 * @param func - The function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Throttles a function call to limit execution frequency
 * @param func - The function to throttle
 * @param limit - Time limit in milliseconds
 * @returns Throttled function
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Generates a random ID string
 * @param length - Length of the ID (default: 8)
 * @returns Random ID string
 */
export const generateId = (length: number = 8): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Validates email format
 * @param email - Email string to validate
 * @returns Boolean indicating if email is valid
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates phone number format (basic validation)
 * @param phone - Phone string to validate
 * @returns Boolean indicating if phone is valid
 */
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

/**
 * Formats a date to a readable string
 * @param date - Date to format
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatDate = (
  date: Date,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }
): string => {
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

/**
 * Converts a string to kebab-case
 * @param str - String to convert
 * @returns Kebab-case string
 */
export const toKebabCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

/**
 * Converts a string to camelCase
 * @param str - String to convert
 * @returns Camel-case string
 */
export const toCamelCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());
};

/**
 * Checks if a value is empty (null, undefined, empty string, empty array, empty object)
 * @param value - Value to check
 * @returns Boolean indicating if value is empty
 */
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Deep clones an object
 * @param obj - Object to clone
 * @returns Cloned object
 */
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as T;
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as T;
  if (typeof obj === 'object') {
    const clonedObj = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
  return obj;
};

/**
 * Delays execution for a specified time
 * @param ms - Milliseconds to delay
 * @returns Promise that resolves after the delay
 */
export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Toggles a boolean value
 * @param value - Current boolean value
 * @returns Opposite boolean value
 */
export const toggleBoolean = (value: boolean): boolean => {
  return !value;
};

/**
 * Safely accesses nested object properties
 * @param obj - Object to access
 * @param path - Path to the property (e.g., 'user.profile.name')
 * @param defaultValue - Default value if path doesn't exist
 * @returns Value at the path or default value
 */
export const getNestedValue = (
  obj: any,
  path: string,
  defaultValue: any = undefined
): any => {
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      return defaultValue;
    }
  }
  
  return result;
};

/**
 * Removes HTML tags from a string
 * @param html - HTML string
 * @returns Clean text without HTML tags
 */
export const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

/**
 * Formats file size in human-readable format
 * @param bytes - Size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Interface for user information from contact forms
 */
export interface UserInfo {
  name: string;
  phoneNumber: string;
  email: string;
  inquiry: string;
  message: string;
}

/**
 * Creates an email with the specified format for contact form submissions
 * @param userInfo - Object containing user contact information and message
 * @returns Promise that resolves with the email data
 */
export const createContactEmail = async (userInfo: UserInfo): Promise<{
  from: string;
  to: string;
  subject: string;
  body: string;
}> => {
  // Validate required fields
  if (!userInfo.name || !userInfo.phoneNumber || !userInfo.email || !userInfo.inquiry || !userInfo.message) {
    throw new Error('All fields are required: name, phoneNumber, email, inquiry, message');
  }

  // Format the email body according to the template
  const emailBody = `Name: ${userInfo.name}
Phone Number: ${userInfo.phoneNumber}
Email Address: ${userInfo.email}

${userInfo.message}`;

  // Return the email data
  return {
    from: 'admin@zemanticsllc.com',
    to: 'ben.l@itlize.com',
    subject: userInfo.inquiry,
    body: emailBody
  };
};

/**
 * Sends an email using the Node.js server
 * @param emailData - Email data object containing from, to, subject, and body
 * @returns Promise that resolves when email is sent successfully
 */
export const sendEmail = async (emailData: {
  from: string;
  to: string;
  subject: string;
  body: string;
}): Promise<void> => {
  try {

    console.log('sharedFunctions.ts::sendEmail::emailData', emailData);

    const url = CONTACT_API_URL;

    const response = await axios.post(url,
    { 
      name: emailData.from,
      email: emailData.from,
      phoneNumber: '', // Will be provided by the form
      inquiry: emailData.subject,
      message: emailData.body
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('Email sent successfully:', response.data.messageId);
  } catch (error) {
    console.error('Error sending email:', error);
    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data?.message || error.message;
      throw new Error(`Failed to send email: ${errorMessage}`);
    }
    throw new Error(`Failed to send email: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Sends contact form data directly to the Node.js server
 * @param userInfo - Object containing user contact information and message
 * @returns Promise that resolves when email is sent successfully
 */
export const sendContactForm = async (userInfo: UserInfo): Promise<void> => {
  try {
    console.log('sharedFunctions.ts::sendContactForm::userInfo', userInfo);

    const url = CONTACT_API_URL;

    const response = await axios.post(url, userInfo, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('Contact form sent successfully:', response.data.messageId);
  } catch (error) {
    console.error('Failed to send contact form:', error);
    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data?.message || error.message;
      throw new Error(`Failed to send email: ${errorMessage}`);
    }
    throw error;
  }
};

/**
 * Creates and sends an email with the specified format for contact form submissions
 * @param userInfo - Object containing user contact information and message
 * @returns Promise that resolves when email is sent successfully
 */
export const createAndSendContactEmail = async (userInfo: UserInfo): Promise<void> => {
  try {
    // Use the new sendContactForm function
    await sendContactForm(userInfo);
    
    console.log('Contact email sent successfully');
  } catch (error) {
    console.error('Failed to send contact email:', error);
    throw error;
  }
}; 